import type {
  Editor,
  InputDescription,
  IntegrationDescription,
  SelectEditorOption,
  StatusEditorOption,
  TagEditorOption,
} from "@smartsuite/automation-engine-api";
import {
  SelectEditorDescription_Type,
  SplitType,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import { checkInputValidation } from "../../../../../../../../stores/automation/utils/inputValidation";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import {
  DropdownPanel,
  MenuGroupContent,
  MenuOption,
  MenuOptionWrapper,
  Paragraph,
  PickerInputField,
} from "@smartsuite/react-ui/lib";
import type { PillPickerEditorCommands } from "@smartsuite/smartdoc";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { SolutionColorVariables } from "@smartsuite/types";
import { t } from "i18next";
import type React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as R from "remeda";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { PickerStyleWrapper } from "../../../../../../../../components/PickerStyleWrapper";
import { useTheme } from "../../../../../../../../stores";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type { DynamicSection } from "../../../../../../../../types/line";
import { useConverters } from "../../../../../../../../utils/conditions-coverter.hook";
import { ActionLabel } from "../../../../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import { usePillTooltip } from "../../../../../../../../utils/make-tooltip.hook";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import { POSITION_AT, POSITION_MY } from "../../../../style";
import {
  convertActionsToDynamicSections,
  convertTriggerToDynamicSection,
} from "../../../../utils";
import { StyledContainerWrapper } from "../../../style";
import { ActionOptions } from "../../containers/ActionOptions";
import { TriggerOptions } from "../../containers/TriggerOptions";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import { SimpleSelectEditor } from "../SimpleSelectEditor";
import {
  calculateConfigValue,
  checkIfIsPickerInputField,
  ifLoopingSourceIsActionFind,
} from "../utils";
import { generalTypes, ssTypes } from "./select-output-types";
import { StyledContainer, StyledOption } from "./style";
import { CurrentOption } from "../../containers/CurrentOption";
import { mergeNextPillToInput } from "../NumberEditor/utils";
import { Tab } from "../../containers/TriggerDropdown/TriggerDropdown";
import { useLoopingMenu } from "../../../../hooks/use-looping-menu";
import {
  actionSourceBoundary,
  canOfferActionSources,
  resolveEditorLoopScope,
} from "../../../../utils/action-sources";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";

export const SelectEditor: React.FC<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  /** Set by ArgumentEditor when `actions` is an explicit, already-scoped list
   * (Conditional Logic branch conditions): offer it whole even though there is
   * no owning `action` to bound it (BUG-21770). */
  actionsArePreScoped?: boolean;
  /** The loop whose Current Item this editor may offer, used verbatim when
   * `actionsArePreScoped` is set. See useLoopingMenu (BUG-21770). */
  loopScope?: LoopScope;
  integrations: IntegrationDescription[];
  description?: InputDescription;
  editor?: Editor;
  isMultiple: boolean;
  isError?: boolean;
  onClickTrash?: () => void;
  type?: SelectEditorDescription_Type;
  disableSplit?: boolean;
  placeholder?: string;
  showValidationError?: boolean;
}> = ({
  input,
  onChange,
  trigger,
  actions,
  action,
  actionsArePreScoped,
  loopScope,
  integrations,
  description,
  editor,
  isMultiple,
  isError,
  onClickTrash,
  type,
  disableSplit,
  placeholder,
  showValidationError,
}) => {
  const store = useRootStore();
  const theme = useTheme();
  const buttonRef = useRef();
  const commandsRef = useRef<PillPickerEditorCommands>();
  const [isOpen, setIsOpen] = useState(false);
  const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
  const [actionMenus, setActionMenus] = useState<DynamicSection[]>([]);
  const [options, setOptions] = useState<
    SelectEditorOption[] | StatusEditorOption[] | TagEditorOption[]
  >([]);
  const [pills, setPills] = useState<PillNodeAttrs[]>([]);

  const [tab, setTab] = useState<
    "root" | "trigger-fields" | "action-fields" | "options" | "current"
  >("root");
  const [search, setSearch] = useState<string>("");
  const [unselectedOptions, setUnselectedOptions] = useState<
    SelectEditorOption[] | StatusEditorOption[] | TagEditorOption[]
  >([]);
  const [selectedActionMenu, setSelectedActionMenu] = useState<
    DynamicSection | undefined
  >();
  const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
    useFieldMissingValidation();
  const [focus, setFocus] = useState<boolean>(false);
  const [actionType, setActionType] = useState<
    | "replace"
    | "clear"
    | "append"
    | "mathAdd"
    | "mathSubtract"
    | "mathMultiply"
    | "mathDivide"
    | undefined
  >(input.operatorConfig?.operator?.oneofKind);
  const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
  const [validationError, setValidationError] = useState("");
  const rootLooping = store.automations.candidate.looping;
  const looping = resolveEditorLoopScope(
    action,
    rootLooping,
    loopScope,
    actionsArePreScoped,
  );

  const { valueConfigToTargetPills } = useConverters();

  const { makeTriggerPillTooltip, makeActionPillTooltip } = usePillTooltip();

  const { loopingMenu } = useLoopingMenu({
    loopScope,
    actionsArePreScoped,
    trigger,
    actions,
    integrations,
    types: generalTypes,
    ssFieldTypes: ssTypes,
    systemAllowedOptions: [],
    action,
  });

  useLayoutEffect(() => {
    if (
      input.value?.values.length === 0 &&
      description?.editor?.editor.oneofKind === "select" &&
      description?.editor?.editor.select.default !== undefined
    ) {
      const defaultValue = description.editor.editor.select.default;
      const defaultOption = description.editor.editor.select.options.find(
        (option) => option.value === defaultValue,
      );
      if (defaultOption !== undefined) {
        onChange({
          ...input,
          value: {
            values: [
              {
                value: {
                  oneofKind: "string",
                  string: defaultValue,
                },
              },
            ],
          },
        });
      }
    }
  });

  useEffect(() => {
    setTab(unselectedOptions.length ? "options" : "root");
  }, [isOpen]);

  useEffect(() => {
    if (tab === "options") {
      if (unselectedOptions.length) {
        setTab("options");
      } else {
        setTab("root");
      }
    }
  }, [unselectedOptions]);

  useEffect(() => {
    setUnselectedOptions(
      options.filter(
        (option) =>
          pills.find((pill) => pill.title === option.label) === undefined,
      ),
    );
  }, [options, pills]);

  useEffect(() => {
    if (actionType === undefined) return;
    if (actionType === "clear") {
      setIsOpen(false);
      setIsCleanAction(true);
    } else {
      setIsCleanAction(false);
    }
    onChange({
      ...input,
      value:
        actionType === "clear"
          ? {
              values: [],
            }
          : input.value,
      operatorConfig: {
        // @ts-expect-error
        operator: {
          oneofKind: actionType,
          [actionType]: {},
        },
      },
    });
  }, [actionType, isOpen]);

  const addPill = (attrs: PillNodeAttrs): void => {
    let newPills: PillNodeAttrs[];

    if (attrs.title !== undefined) {
      // @ts-expect-error
      attrs.origTitle = attrs.title;
      attrs.title = getLabel(attrs.title);
      const value = JSON.parse(attrs.value);
      if (value.target === "trigger") {
        attrs.tooltip = makeTriggerPillTooltip(trigger);
      } else if (value.target === "action") {
        const action = actions.find((item) => item.hash === value.hash);
        if (action !== undefined) {
          attrs.tooltip = makeActionPillTooltip(action);
        }
      }
    }

    if (isMultiple) {
      newPills = [...pills, attrs];
    } else {
      newPills = [attrs];
      setTimeout(() => setIsOpen(false));
    }
    setPills(newPills);

    onChange({
      ...input,
      value: calculateConfigValue(newPills, actions, trigger),
    });
  };

  const removePill = (_: number, pill: FieldTypePillProps): void => {
    const newPills = pills.filter((item) => item !== pill);
    setPills(newPills);
    onChange({
      ...input,
      value: calculateConfigValue(newPills, actions, trigger),
    });
  };

  useEffect(() => {
    setTriggerMenu(
      convertTriggerToDynamicSection(
        trigger,
        integrations,
        generalTypes,
        generalTypes,
        ssTypes,
        undefined,
        [
          ...(input.valueDescription !== undefined
            ? [
                {
                  sourceType: "formulafield",
                  types: [
                    ValueDescription_T.TAG,
                    ValueDescription_T.STATUS,
                    ValueDescription_T.SELECT,
                  ],
                },
                {
                  sourceType: "lookupfield",
                  types: [
                    ValueDescription_T.TAG,
                    ValueDescription_T.STATUS,
                    ValueDescription_T.SELECT,
                  ],
                },
              ]
            : []),
        ],
      ),
    );

    if (canOfferActionSources(action, actionsArePreScoped)) {
      setActionMenus(
        convertActionsToDynamicSections(
          actions,
          actionSourceBoundary(action, actionsArePreScoped),
          integrations,
          generalTypes,
          ssTypes,
          [],
          looping,
        ),
      );
    }
  }, [trigger, integrations, input.value, actionsArePreScoped, loopScope]);

  useEffect(() => {
    if (R.isNil(input.value)) return;

    setPills(
      valueConfigToTargetPills(
        input.value,
        trigger,
        actions,
        store.automations.candidate.getTimezone(),
        getLabel,
        input,
        action?.actionReference,
        action,
        loopingMenu?.options,
      ),
    );
  }, [input.value, trigger, actions]);

  useEffect(() => {
    if (description?.editor?.editor.oneofKind === "select") {
      setOptions(description?.editor?.editor.select.options);
    }
    if (editor?.editor.oneofKind === "select") {
      setOptions(editor.editor.select.options);
    }

    if (description?.editor?.editor.oneofKind === "status") {
      setOptions(description?.editor?.editor.status.options);
    }
    if (editor?.editor.oneofKind === "status") {
      setOptions(editor.editor.status.options);
    }

    if (description?.editor?.editor.oneofKind === "tag") {
      setOptions(description?.editor?.editor.tag.options);
    }
    if (editor?.editor.oneofKind === "tag") {
      setOptions(editor.editor.tag.options);
    }
  }, [description, editor]);

  useEffect(() => {
    if (store.inputToFocus?.inputId === input.inputId) {
      setIsOpen(true);
      store.inputToFocus = undefined;
    }
  }, []);

  useEffect(() => {
    if (showValidationError && description !== undefined) {
      setValidationError(checkInputValidation(description, input));
    } else {
      setValidationError("");
    }
  }, [input, description, showValidationError]);

  const getLabel = (label: string): string => {
    if (label === "Yes") {
      return t("field-popups.yes");
    } else if (label === "No") {
      return t("field-popups.no");
    }
    return label;
  };

  const changeActionType = (
    type:
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined,
  ): void => {
    setActionType(type);
  };

  const triggerOutputs =
    triggerMenu?.options.filter((option) => !option.contextSpecific) ?? [];

  const disabledOptionId = pills.map((pill) => pill.title);

  const buildMenu = (): React.ReactNode => {
    return (
      <DropdownPanel
        targetElementRef={buttonRef}
        isActive={isOpen}
        at={POSITION_AT}
        my={POSITION_MY}
        minWidth={300}
        arrowBackground="custom"
        arrowCustomBackground={SolutionColorVariables.sc1}
        panelLineBackground={SolutionColorVariables.sc1}
        portalStyles={getLocalCssColorVariables(theme)}
        onClickOutside={(event) => {
          !checkIfIsPickerInputField(event.target, input.inputId) &&
            setIsOpen(false);
        }}
      >
        {tab === "root" && (
          <MenuOptionWrapper>
            {triggerOutputs.length > 0 && (
              <MenuOption
                submenuArrow="right"
                onClick={() => setTab("trigger-fields")}
              >
                {t("automation-form.fields-from-trigger")}
              </MenuOption>
            )}

            {actionMenus.map(
              (actionMenu) =>
                actionMenu?.options.length > 0 && (
                  <MenuOption
                    key={actionMenu.hash}
                    submenuArrow="right"
                    onClick={() => {
                      setSelectedActionMenu(actionMenu);
                      setTab("action-fields");
                    }}
                  >
                    {actionMenu.hideNamePrefix === true
                      ? actionMenu.name
                      : t("field-popups.action-popup", {
                          name: actionMenu.name,
                        })}
                  </MenuOption>
                ),
            )}
            {options.length > 0 && (
              <MenuOption
                key="Select from field’s Choices"
                submenuArrow="right"
                onClick={() => setTab("options")}
              >
                {t("editor.field-values")}
              </MenuOption>
            )}
            <CurrentOption
              loopingMenu={loopingMenu}
              loopScope={looping}
              actionsArePreScoped={actionsArePreScoped}
              addPill={(looping) => {
                // Case when source for looping is the find action
                if (
                  ifLoopingSourceIsActionFind(looping) ||
                  loopingMenu?.options?.length
                ) {
                  setTab("current");
                  return;
                }

                if (looping.source) {
                  onChange(
                    mergeNextPillToInput(input, {
                      type: "loop",
                      looping,
                    }),
                  );
                }
              }}
            />
          </MenuOptionWrapper>
        )}
        {tab === "trigger-fields" && (
          <TriggerOptions
            searchPlaceholder={t("automation-form.search-fields")}
            title={t("automation-form.fields-from-trigger")}
            onNavigateBack={() => setTab("root")}
            addPill={addPill}
            options={triggerOutputs}
            disabledOptionId={disabledOptionId}
          />
        )}
        {tab === "current" && loopingMenu && (
          <TriggerOptions
            tab={Tab.LOOPING_FIELDS}
            searchPlaceholder={"Search Fields"}
            title={loopingMenu.name}
            onNavigateBack={() => setTab("root")}
            addPill={addPill}
            options={loopingMenu.options}
            hasSingleMenuItem={false}
            loopingMenuHash={loopingMenu.hash}
            disabledOptionId={disabledOptionId}
            loopingMenu={loopingMenu}
          />
        )}
        {tab === "action-fields" && (
          <ActionOptions
            onNavigateBack={() => setTab("root")}
            addPill={addPill}
            selectedActionMenu={selectedActionMenu}
            selectedActionHash={selectedActionMenu?.hash}
          />
        )}
        {tab === "options" && (
          <MenuGroupContent
            title={t("editor.field-values")}
            onNavigateBack={
              triggerOutputs.length === 0 ? () => {} : () => setTab("root")
            }
            search={unselectedOptions.length > 5}
            onSearchChange={(text: string) => setSearch(text)}
            hideBackIcon={triggerOutputs.length === 0}
          >
            {unselectedOptions
              .filter((option) =>
                search.length > 0
                  ? option.label
                      .toLocaleLowerCase()
                      .includes(search.toLocaleLowerCase())
                  : true,
              )
              .map((option) => (
                <StyledOption key={`option-${option.label}-${option.value}`}>
                  <MenuOption
                    className="automation-select-option"
                    onClick={() =>
                      addPill({
                        icon: "",
                        title: option.label,
                        value: JSON.stringify({
                          value: option.value,
                          target:
                            editor?.editor.oneofKind === "tag"
                              ? "tag"
                              : "option",
                        }),
                      })
                    }
                  >
                    {/* @ts-expect-error */}
                    <StyledContainer color={option.color ?? ""}>
                      {getLabel(option.label)}
                    </StyledContainer>
                  </MenuOption>
                </StyledOption>
              ))}
          </MenuGroupContent>
        )}
      </DropdownPanel>
    );
  };

  return type === SelectEditorDescription_Type.DROPDOWN ? (
    <SimpleSelectEditor
      input={input}
      onChange={onChange}
      description={description}
      editor={editor}
      onClickTrash={onClickTrash}
      isError={Boolean(isError)}
      validationError={validationError}
      placeholder={placeholder}
    />
  ) : (
    <StyledContainerWrapper>
      {description !== undefined && (
        <CaptionBlock
          description={description}
          onClickTrash={onClickTrash}
          splitType={input.splitType ?? SplitType.NONE}
          createNewValues={input.createNewValues}
          onCreateNewValuesChange={(supportNewValues) => {
            onChange({
              ...input,
              createNewValues: supportNewValues,
            });
          }}
          onSplitChange={
            isMultiple && !disableSplit
              ? (splitType) => {
                  onChange({
                    ...input,
                    splitType,
                  });
                }
              : undefined
          }
        />
      )}
      <PickerStyleWrapper>
        <PickerInputField
          showAddButton={!isCleanAction}
          className={
            isError ||
            validationError !== "" ||
            !isInputValidByFieldMissing(input, action, !!editor)
              ? `is-error picker-input-field-${input.inputId} ${
                  isCleanAction ? "is-clear" : ""
                }`
              : `picker-input-field-${input.inputId} ${
                  isCleanAction ? "is-clear" : ""
                }`
          }
          state={
            isError ||
            validationError !== "" ||
            !isInputValidByFieldMissing(input, action, !!editor)
              ? "error"
              : isOpen || focus
                ? "active"
                : "default"
          }
          buttonRef={buttonRef}
          // @ts-expect-error
          commandsRef={commandsRef}
          isOpen={isOpen}
          pills={pills}
          pillStyle={{
            textSize: "m",
          }}
          onButtonClick={() => setIsOpen((open) => !open)}
          onPillRemove={removePill}
          placeholder={
            isCleanAction
              ? t("automation-form.clean-operator-editor-placeholder")
              : isOpen
                ? t("automation-form.select-field-placeholder")
                : ""
          }
          onClick={() => {
            setIsOpen(true);
          }}
        >
          {action?.actionReference?.actionId === ActionLabel.updateRecord &&
            description?.operators?.operators !== undefined &&
            description?.operators?.operators.length > 0 && (
              <UpdateValueDropdown
                operatorConfig={input.operatorConfig}
                operatorDescriptions={description?.operators.operators}
                onOpenedChange={setFocus}
                onChange={changeActionType}
              />
            )}

          {isOpen && buildMenu()}
        </PickerInputField>
        {validationError !== "" && (
          <Paragraph color={"error"} size={"xs"}>
            {validationError}
          </Paragraph>
        )}
        <FieldMissingError
          message={getFieldMissingErrorMessage(input, action, !!editor)}
        />
      </PickerStyleWrapper>
    </StyledContainerWrapper>
  );
};
